import React, { useState, useEffect, useCallback } from "react";
import {
  Upload, FileText, CheckCircle, XCircle, Loader2,
  Shield, Clock, ChevronRight, AlertTriangle, LogOut, Home, ShieldCheck
} from "lucide-react";
import { Link } from "react-router-dom";
import PolicySignStep from "./PolicySignStep";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: "aadhaar", label: "Aadhaar Card", desc: "Upload a clear photo of your Aadhaar card (front side)" },
  { key: "pan", label: "PAN Card", desc: "Upload a clear photo of your PAN card" },
  { key: "photo", label: "Photo ID", desc: "Upload a recent passport-size photograph" },
  { key: "policies", label: "Company Policies", desc: "Read and e-sign all 6 Big Kreation policies" },
  { key: "rules", label: "Rules & Regulations", desc: "Read and accept the company rules and NDA" },
];

const RULES_TEXT = [
  "All customer data accessed through DoozyGo systems is strictly confidential and must not be shared externally.",
  "Login credentials must not be shared with anyone. Each team member must use their own account.",
  "All booking modifications, cancellations, and financial changes must be logged and require Admin approval.",
  "Customer complaints must be reported immediately through the official channel. Do not resolve payment disputes independently.",
  "Company assets, brand materials, and proprietary processes must not be used for personal or third-party purposes.",
  "Any suspicious activity or security breach must be reported to the Security team within 1 hour of discovery.",
  "Unauthorized data exports, screenshots of financial data, or sharing booking details on personal devices is strictly prohibited.",
  "Failure to comply with these rules may result in immediate termination and legal action.",
];

const NDA_TEXT = "I understand that all information, processes, customer data, vendor details, pricing strategies, and business operations of DoozyGo are confidential. I agree not to disclose, copy, or use any such information for personal benefit or share it with any third party during and after my engagement with DoozyGo.";

const OnboardingWizard = ({ auth, onLogout }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [policiesComplete, setPoliciesComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auth headers — pass JWT as fallback when cookies are blocked (iOS private, etc.)
  const authHeaders = useCallback(() => (
    auth?.token ? { "X-Admin-Auth": auth.token } : {}
  ), [auth]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/status`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setRulesAccepted(data.rules_accepted || false);
        setNdaAccepted(data.nda_accepted || false);
        // Auto-advance to first incomplete step
        const docs = data.documents || {};
        if (!docs.aadhaar) setActiveStep(0);
        else if (!docs.pan) setActiveStep(1);
        else if (!docs.photo) setActiveStep(2);
        else setActiveStep(3);
      } else {
        console.warn('[Onboarding] Status fetch HTTP', res.status);
      }
    } catch (e) { console.error('[Onboarding] Status fetch failed:', e.message); }
    setLoading(false);
  }, [authHeaders]);

  // Quick polices-complete check
  const fetchPoliciesComplete = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/policies`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (res.ok) {
        const d = await res.json();
        setPoliciesComplete(!!d.all_signed);
      }
    } catch (e) { console.warn('[Onboarding] policies check failed:', e.message); }
  }, [authHeaders]);

  useEffect(() => { fetchStatus(); fetchPoliciesComplete(); }, [fetchStatus, fetchPoliciesComplete]);

  const handleUpload = async (docType, file) => {
    setUploading(true);
    try {
      // Size guard — prevent upload of huge phone-camera originals that proxies reject
      if (file.size > 15 * 1024 * 1024) {
        alert(`File is ${(file.size/1024/1024).toFixed(1)} MB — please compress or take a smaller photo (max 15 MB).`);
        setUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append("doc_type", docType);
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/onboarding/upload-document`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: formData,
      });
      let data = {};
      try { data = await res.json(); } catch { /* non-JSON response from a proxy */ }
      if (res.ok) {
        await fetchStatus();
        if (docType === "aadhaar") setActiveStep(1);
        else if (docType === "pan") setActiveStep(2);
        else if (docType === "photo") setActiveStep(3);
      } else {
        const fallback = res.status === 401
          ? "Your session expired. Please log in again."
          : res.status === 413
          ? "File too large for the server. Please use a smaller photo."
          : res.status === 404
          ? "Account not found. Please log out and log in again."
          : `Upload failed (HTTP ${res.status})`;
        alert(data.detail || fallback);
        console.error('[Onboarding] upload error:', res.status, data);
      }
    } catch (err) {
      console.error('[Onboarding] upload network error:', err);
      alert(`Upload failed: ${err?.message || "connection problem"}. Check your network and try again.`);
    }
    setUploading(false);
  };

  const handleAcceptRules = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/accept-rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ rules_accepted: rulesAccepted, nda_accepted: ndaAccepted }),
      });
      let data = {};
      try { data = await res.json(); } catch { /* empty */ }
      if (res.ok) {
        await fetchStatus();
      } else {
        alert(data.detail || `Submit failed (HTTP ${res.status})`);
        console.error('[Onboarding] accept-rules error:', res.status, data);
      }
    } catch (err) {
      console.error('[Onboarding] accept-rules network error:', err);
      alert(`Submit failed: ${err?.message || "connection problem"}.`);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E83E8C]" size={40} />
      </div>
    );
  }

  // If status is pending_approval or approved or rejected, show status screen
  if (status?.verification_status === "pending_approval") {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center" data-testid="onboarding-pending">
          <Clock size={48} className="text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1E1B4B] mb-2">Application Under Review</h2>
          <p className="text-[#475569] mb-6">
            Your documents have been submitted successfully. An Admin will review and approve your application shortly.
          </p>
          <div className="bg-orange-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-orange-700">You'll be notified once your application is approved. Please check back later.</p>
          </div>
          <button onClick={() => onLogout("manual")} className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-[#475569] rounded-xl font-medium mx-auto hover:bg-gray-200">
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (status?.verification_status === "rejected") {
    return (
      <div className="min-h-screen bg-[#FFFAF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center" data-testid="onboarding-rejected">
          <XCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1E1B4B] mb-2">Application Rejected</h2>
          <p className="text-[#475569] mb-4">
            Your application was not approved. Please contact your Admin for more details.
          </p>
          {status.rejection_reason && (
            <div className="bg-red-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-red-700 font-medium">Reason:</p>
              <p className="text-sm text-red-600">{status.rejection_reason}</p>
            </div>
          )}
          <button onClick={() => onLogout("manual")} className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-[#475569] rounded-xl font-medium mx-auto hover:bg-gray-200">
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  const docs = status?.documents || {};

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="onboarding-wizard">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_party-in-2hrs/artifacts/47ayyz1k_SUMIT%20DOOZYGO%20LOGO%20-%20AUG%202023.png" alt="DoozyGo" className="h-10" />
            <div>
              <h1 className="text-lg font-bold text-[#1E1B4B]">Welcome, {auth.name || auth.username}</h1>
              <p className="text-xs text-[#94a3b8]">Complete your onboarding to get started</p>
            </div>
          </div>
          <button onClick={() => onLogout("manual")} className="text-[#475569] hover:text-[#E83E8C]"><LogOut size={20} /></button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, i) => {
            let isComplete;
            if (i < 3) isComplete = !!docs[step.key];
            else if (i === 3) isComplete = policiesComplete;
            else isComplete = status?.rules_accepted && status?.nda_accepted;
            const isCurrent = activeStep === i;
            return (
              <React.Fragment key={step.key}>
                <button
                  onClick={() => setActiveStep(i)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${isComplete ? "bg-green-500 text-white" : isCurrent ? "bg-[#E83E8C] text-white ring-4 ring-[#E83E8C]/20" : "bg-gray-200 text-gray-500"}`}
                  data-testid={`step-${step.key}`}
                >
                  {isComplete ? <CheckCircle size={18} /> : i + 1}
                </button>
                {i < STEPS.length - 1 && (
                  <div className={`w-12 h-1 rounded-full ${isComplete ? "bg-green-500" : "bg-gray-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
          {activeStep < 3 ? (
            // Document Upload Steps
            <div>
              <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">{STEPS[activeStep].label}</h2>
              <p className="text-[#475569] text-sm mb-6">{STEPS[activeStep].desc}</p>

              {docs[STEPS[activeStep].key] ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                  <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
                  <p className="font-semibold text-green-700">Uploaded Successfully</p>
                  <p className="text-sm text-green-600 mt-1">{docs[STEPS[activeStep].key].filename}</p>
                  <button
                    onClick={() => setActiveStep(prev => Math.min(prev + 1, 3))}
                    className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white rounded-xl font-medium mx-auto hover:bg-green-600"
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                </div>
              ) : (
                <label className={`block border-2 border-dashed ${uploading ? "border-[#E83E8C]" : "border-gray-300 hover:border-[#E83E8C]"} rounded-2xl p-8 text-center cursor-pointer transition-colors`}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUpload(STEPS[activeStep].key, file);
                    }}
                    data-testid={`upload-${STEPS[activeStep].key}`}
                  />
                  {uploading ? (
                    <Loader2 size={40} className="animate-spin text-[#E83E8C] mx-auto mb-3" />
                  ) : (
                    <Upload size={40} className="text-[#94a3b8] mx-auto mb-3" />
                  )}
                  <p className="font-medium text-[#1E1B4B]">{uploading ? "Uploading..." : "Click to upload or drag & drop"}</p>
                  <p className="text-xs text-[#94a3b8] mt-1">JPG, PNG or PDF up to 5MB</p>
                </label>
              )}
            </div>
          ) : activeStep === 3 ? (
            // Company Policies E-sign Step
            <div>
              <PolicySignStep
                prefillName={auth.name || ""}
                authToken={auth?.token || ""}
                onAllSigned={() => { setPoliciesComplete(true); fetchPoliciesComplete(); }}
              />
              <button
                onClick={() => setActiveStep(4)}
                disabled={!policiesComplete}
                className="mt-6 w-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="continue-after-policies"
              >
                Continue to Final Step <ChevronRight size={16} />
              </button>
              {!policiesComplete && (
                <p className="text-xs text-center text-amber-600 mt-2">
                  Please sign all 6 company policies to proceed.
                </p>
              )}
            </div>
          ) : (
            // Rules & NDA Step
            <div>
              <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">Rules & Regulations</h2>
              <p className="text-[#475569] text-sm mb-6">Read carefully and accept to proceed</p>

              <div className="bg-gray-50 rounded-2xl p-5 mb-6 max-h-64 overflow-y-auto">
                <h3 className="font-bold text-sm text-[#1E1B4B] mb-3 flex items-center gap-2"><Shield size={16} className="text-[#E83E8C]" /> Company Rules & Regulations</h3>
                <ol className="space-y-3 text-sm text-[#475569] list-decimal pl-4">
                  {RULES_TEXT.map((rule, i) => (
                    <li key={`rule-${i}`}>{rule}</li>
                  ))}
                </ol>
              </div>

              <label className="flex items-start gap-3 p-4 bg-[#FFFAF5] rounded-xl mb-4 cursor-pointer hover:bg-orange-50 transition-colors" data-testid="rules-checkbox">
                <input
                  type="checkbox"
                  checked={rulesAccepted}
                  onChange={e => setRulesAccepted(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-[#E83E8C]"
                />
                <span className="text-sm text-[#1E1B4B]">I have read and agree to all the Rules & Regulations listed above</span>
              </label>

              <div className="bg-gray-50 rounded-2xl p-5 mb-4">
                <h3 className="font-bold text-sm text-[#1E1B4B] mb-2 flex items-center gap-2"><FileText size={16} className="text-[#E83E8C]" /> Non-Disclosure Agreement (NDA)</h3>
                <p className="text-sm text-[#475569]">{NDA_TEXT}</p>
              </div>

              <label className="flex items-start gap-3 p-4 bg-[#FFFAF5] rounded-xl mb-6 cursor-pointer hover:bg-orange-50 transition-colors" data-testid="nda-checkbox">
                <input
                  type="checkbox"
                  checked={ndaAccepted}
                  onChange={e => setNdaAccepted(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-[#E83E8C]"
                />
                <span className="text-sm text-[#1E1B4B]">I accept the Non-Disclosure Agreement</span>
              </label>

              {/* Check if all docs are uploaded + all policies signed */}
              {(!docs.aadhaar || !docs.pan || !docs.photo || !policiesComplete) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-2 text-sm text-amber-700">
                  <AlertTriangle size={16} />
                  {!policiesComplete
                    ? "Please sign all 6 company policies before submitting."
                    : "Please upload all required documents before submitting."}
                </div>
              )}

              <button
                onClick={handleAcceptRules}
                disabled={!rulesAccepted || !ndaAccepted || !docs.aadhaar || !docs.pan || !docs.photo || !policiesComplete || submitting}
                className="w-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="submit-onboarding-btn"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OnboardingWizard;
